
import $ from '../util/jquery.js';
import log from '../util/log.js';

/**
 * DonationBannerIframeHandler manages the 1-directional flow to the outside handler
 * attached to the origin's window.
 *
 * This is usually
 * @see www/sf/includes/donate.js
 * WAYBACK should have copied companion file above
 *
 * Relevant events to postMessage:
 * - open modal
 * - close modal
 * - set height
 * - hide banner
 * - set cookie
 */
class DonationBannerIframeHandler {
  /**
   * Viewport breakpoint (px) separating the mobile and desktop banners. Must stay in
   * sync with the `@media (max-width: 767px)` rule in banner.css that physically
   * shows/hides each banner.
   */
  static get MOBILE_BREAKPOINT() {
    return 768;
  }

  /**
   * Single source of truth for the "which viewport am I?" decision, shared by the
   * banner selector, the iframe height calc, and donation-banner.js's bannerElement /
   * donationOrigin. Keyed to `window.innerWidth` — the *rendering frame's* width, which
   * is what the banner.css media query evaluates against (whether the banner is inline
   * or embedded in an iframe). Reads its own frame only, so it's cross-origin-safe.
   */
  static is_mobile_viewport() {
    return window.innerWidth < DonationBannerIframeHandler.MOBILE_BREAKPOINT;
  }

  /**
   * Returns the visible-viewport banner's selector. PHP may render both desktop and
   * mobile banners; the hidden one has `display: none` and would report 0 height,
   * so we pick by viewport.
   */
  static get_visible_banner_selector() {
    return DonationBannerIframeHandler.is_mobile_viewport()
      ? '.js-donate-banner--mobile'
      : '.js-donate-banner--desktop';
  }

  static get_banner_height() {
    // Coalesce to 0: when the visible viewport has no banner (a single-modality
    // campaign, or HideBanner on one modality) the selector matches nothing and
    // jQuery's outerHeight() returns undefined, which would post a bad height.
    return $(DonationBannerIframeHandler.get_visible_banner_selector()).outerHeight() || 0;
  }

  /**
   * Emits `postMessage` call to window
   * @param { string } event
   * @param { number } incomingValue, optional
   *
   * Post Message payload = {
   *  event: string - see above for complete list,
   *  value: number,
   *  bannerHeight: number
   * }
   */
  static postMessage(event, incomingValue = 0) {
    log('postin', { event, incomingValue });

    const bannerHeight = DonationBannerIframeHandler.get_banner_height();
    const bannerHeightWithReminderForm =
      DonationBannerIframeHandler.preventResizeEventResponse
        ? DonationBannerIframeHandler.calculateBannerWithReminderForm()
        : 0;

    const value = Math.max(bannerHeightWithReminderForm, incomingValue);

    window.parent.postMessage(JSON.stringify({
      event,
      value,
      bannerHeight,
    }), '*');

    if (event === 'close modal') {
      DonationBannerIframeHandler.adjust_height();
    }
  }

  /**
   * Open reminder form handler
   * turns on `DonationBannerIframeHandler.preventResizeEventResponse`
   */
  static openReminderForm() {
    DonationBannerIframeHandler.preventResizeEventResponse = true;
    DonationBannerIframeHandler.postMessage('set height');
    window.scrollTo(0, 0);
  }

  /**
   * Resets iframe to original original height
   * turns off `DonationBannerIframeHandler.preventResizeEventResponse`
   * & ensures top of banner is seen
   */
  static closeReminderForm() {
    DonationBannerIframeHandler.preventResizeEventResponse = false;
    DonationBannerIframeHandler.postMessage('close modal');
    window.scrollTo(0, 0);
  }

  static onSubmitReminderForm(daysBannerIsHidden = 0) {
    DonationBannerIframeHandler.preventResizeEventResponse = false;
    DonationBannerIframeHandler.postMessage('set cookie', daysBannerIsHidden);
  }

  /**
   * calculate true height of iframe depending on
   * the browser size & the banner's relative media queries.
   * @returns { number } totalBannerHeight
   */
  static calculateBannerWithReminderForm() {
    const selector = DonationBannerIframeHandler.get_visible_banner_selector();
    const banner = document.querySelector(selector);
    const donationForm = document.querySelector('.js-donate-form');
    const reminderForm = document.querySelector('.js-donate-later-dropdown');
    const extraPadding = 15;

    const bannerHeight = banner.offsetHeight;
    const reminderFormHeight = reminderForm.offsetHeight;
    const donationFormTotalHeight = donationForm.offsetHeight + donationForm.offsetTop;
    const spaceToBottomOfBanner = bannerHeight - donationFormTotalHeight;
    const totalBannerHeight = bannerHeight - spaceToBottomOfBanner
      + reminderFormHeight + extraPadding;

    return totalBannerHeight;
  }

  // newHeight is optional
  static adjust_height(newHeight) {
    const banner_height = newHeight ?? DonationBannerIframeHandler.get_banner_height();
    DonationBannerIframeHandler.postMessage('set height', banner_height);
    log({ banner_height });
    this.currentBannerHeight = banner_height;
  }

  static init() {
    log('donato tornado');

    $(window).on('resize  orientationchange', () => {
      DonationBannerIframeHandler.fixBannerHeightIfNeeded();
      clearTimeout(DonationBannerIframeHandler.adjust_height_throttler);
      DonationBannerIframeHandler.adjust_height_throttler = setTimeout(
        DonationBannerIframeHandler.adjust_height, 100,
      );
    });

    this.currentWindowWidth = window.innerWidth;
    this.currentBannerHeight = DonationBannerIframeHandler.get_banner_height();
    DonationBannerIframeHandler.adjust_height();
  }

  /**
   * This method fixes a sporadic bug where, during the initial display of the banner,
   * the height gets calculated incorrectly so the banner gets really tall,
   * then shrinks down to normal size.
   *
   * The idea behind the fix is this:
   * If the window width hasn't changed, the height of the banner should not have changed.
   * If we detect that the height calculation has changed, this means the
   * calculation corrected itself so immediately send the adjust_height() command to correct it
   * instead of waiting for the throttler to fire.
   *
   * This bug seems to have an observer effect. The act of getting the banner height
   * here seems to correct the calculation. I have yet to see the adjustment called,
   * but if you remove the `get_banner_height()` call, it starts again.
   */
  static fixBannerHeightIfNeeded() {
    if (window.innerWidth === this.currentWindowWidth) {
      const newHeight = DonationBannerIframeHandler.get_banner_height();
      if (newHeight !== this.currentBannerHeight) {
        log('Performing banner height correction: current, new', this.currentBannerHeight, newHeight);
        DonationBannerIframeHandler.adjust_height(newHeight);
      }
    } else {
      this.currentWindowWidth = window.innerWidth;
    }
  }
}

/**
 * Top level flag toggles when remind me later form is open.
 * This prevents the constant resize event stream from changing
 * the banner's height arbitrarily
*/
DonationBannerIframeHandler.preventResizeEventResponse = false;

export default DonationBannerIframeHandler;
